/**
 * hydrateTableGrid — pure join of schema, current values, and agent overlay.
 *
 * This is the SINGLE reconciliation point for the four-way key vocabulary
 * (design §11):
 *   - `rowLabel`  ↔ `rowDef.label` (display string)
 *   - `colKey`    ↔ `TableColumnDef.key` (schema key)
 *   - `namedRange` = `rowDef.cells[colKey]` (the composite named range, e.g. `Studio_Rent_Month`)
 *   - `displayPath` = `${table}.${row}.${col}` (NOT a valid inputValues key — never used here)
 *
 * D1: non-numeric dataType columns (date / boolean / location) are marked
 * `editable: false` and EXCLUDED from `writes` (visible but not sent to the endpoint).
 */

import type { TableInputDefinition } from '@arcanum-hq/shared-types';

export interface HydratedCell {
  /** Display label for the row (e.g. "Studio") */
  rowLabel: string;
  /** Schema column key (e.g. "rent") */
  colKey: string;
  /** DataType from TableColumnDef */
  dataType: string;
  /**
   * The composite named range for this cell (e.g. `Studio_Rent_Month`).
   * This is the KEY used in `inputValues` / `inputProvenance` / `writes`.
   */
  namedRange: string;
  /** Current model value from `useConversationViews().inputs[namedRange].value`
   *  (falls back to `rowDef.defaultValues[colKey]` when not yet set by the user). */
  currentValue: number | string | boolean | undefined;
  /** Agent-proposed overlay value from `proposal.cells[rowLabel][colKey]`.
   *  undefined when the agent had no opinion about this cell. */
  overlayValue: number | string | boolean | undefined;
  /**
   * Resolved display seed value. FROZEN-PROPOSAL precedence (table-input-ux
   * immutable-history rework, 2026-06-08): the card always shows the agent's
   * proposal-at-turn — `overlayValue` (the suggestion) over the schema default
   * `currentValue`. It deliberately does NOT mirror the live model: a proposal
   * card is a point-in-time transcript entry like every other chat component,
   * so it must read the same after the user applies/edits as it did when the
   * agent first proposed it. Live edits surface as a SEPARATE `[table-edit:*]`
   * receipt at the end of the thread (NarrativeBar/ChatThread), not by mutating
   * this frozen card. When `overlayCells` is omitted (the re-edit editor seeds
   * from current values), `currentValue` is the seed.
   */
  displayValue: number | string | boolean | undefined;
  /**
   * True when this cell's named range carries a `source: 'user'` provenance
   * record in the live model (i.e. the user/agent has applied a value to it).
   * Drives the proposal card's applied → frozen-read-only state derivation
   * (NOT the display value, which stays frozen to the proposal). Read from
   * `useConversationViews().inputs[namedRange].source`.
   */
  applied: boolean;
  /** True when the cell can be edited and included in `writes` (numeric dataTypes only — D1). */
  editable: boolean;
}

export interface HydratedRow {
  rowLabel: string;
  cells: HydratedCell[];
}

export interface HydratedGrid {
  tableDef: TableInputDefinition;
  rows: HydratedRow[];
}

/** Numeric dataTypes eligible for direct write (D1). */
const NUMERIC_DATA_TYPES = new Set([
  'integer',
  'decimal',
  'currency',
  'currency_per_sf',
  'currency_per_unit_year',
  'percentage',
  'months',
  'years',
  'number',
]);

/**
 * Build the full hydrated grid by joining:
 *   1. `tableDef` — column + row schema (dataTypes, named ranges, defaultValues)
 *   2. `currentInputs` — `Record<namedRange, { value: unknown }>` from `useConversationViews().inputs`
 *   3. `overlayCells` — `proposal.cells` (agent-suggested per-cell values, sparse subset OK)
 *
 * @param tableDef   Full TableInputDefinition from `useModel(modelId).data.tableInputs`
 * @param currentInputs  The `inputs` map from `useConversationViews()` — keyed by named range
 * @param overlayCells   Agent's `propose_table_fill` `cells` field (rowLabel → colKey → value); may be sparse
 */
export function hydrateTableGrid(
  tableDef: TableInputDefinition,
  currentInputs:
    | Record<string, { value: unknown; source?: string }>
    | undefined,
  overlayCells:
    | Record<string, Record<string, number | string | boolean>>
    | undefined
): HydratedGrid {
  const rows: HydratedRow[] = tableDef.rows
    .filter((rowDef) => rowDef.active)
    .map((rowDef) => {
      const overlayRow = overlayCells?.[rowDef.label] ?? {};
      const cells: HydratedCell[] = tableDef.columns.map((colDef) => {
        const namedRange = rowDef.cells[colDef.key];
        const editable = NUMERIC_DATA_TYPES.has(colDef.dataType);

        // Priority: current provenance value > default value
        let currentValue: number | string | boolean | undefined;
        const inputRecord = namedRange
          ? currentInputs?.[namedRange]
          : undefined;
        if (inputRecord !== undefined) {
          const v = inputRecord.value;
          if (v !== null && v !== undefined) {
            currentValue = v as number | string | boolean;
          }
        }
        // Fall back to schema default
        if (currentValue === undefined) {
          const def = rowDef.defaultValues[colDef.key];
          if (def !== null && def !== undefined) {
            currentValue = def as number | string | boolean;
          }
        }

        // The cell is "applied" when the live model carries a user-source
        // provenance value for it. Once applied, the live value is the SSOT.
        const applied = inputRecord?.source === 'user';

        const overlayValue =
          overlayRow[colDef.key] !== undefined
            ? (overlayRow[colDef.key] as number | string | boolean)
            : undefined;

        // FROZEN-PROPOSAL precedence: agent overlay (the suggestion) over the
        // schema default. The proposal card is a point-in-time transcript entry
        // — it never mirrors the live model (immutable-history rework). When
        // `overlayCells` is omitted (re-edit editor), `currentValue` is the seed.
        const displayValue =
          overlayValue !== undefined ? overlayValue : currentValue;

        return {
          rowLabel: rowDef.label,
          colKey: colDef.key,
          dataType: colDef.dataType,
          namedRange: namedRange ?? '',
          currentValue,
          overlayValue,
          displayValue,
          applied,
          editable,
        };
      });

      return { rowLabel: rowDef.label, cells };
    });

  return { tableDef, rows };
}

/**
 * Compute per-column totals for a table's `sum` aggregates — generic over any
 * `TableInputDefinition`. Returns `colKey → sum` for every column that is the
 * `targetColumn` of a `sum` aggregate (e.g. UnitMix → `{ units: 250 }`). Empty
 * object when the table declares no sum aggregates. Non-finite cell values
 * (blanks, strings) contribute 0. (user feedback 2026-06-09 — show table totals.)
 */
export function computeColumnSums(
  tableDef: TableInputDefinition,
  rows: HydratedRow[]
): Record<string, number> {
  const sums: Record<string, number> = {};
  for (const agg of tableDef.aggregates ?? []) {
    if (agg.aggregation !== 'sum') continue;
    sums[agg.targetColumn] = rows.reduce((acc, row) => {
      const cell = row.cells.find((c) => c.colKey === agg.targetColumn);
      const v = Number(cell?.displayValue);
      return acc + (Number.isFinite(v) ? v : 0);
    }, 0);
  }
  return sums;
}

/**
 * Build the `writes` map (namedRange → value) from the hydrated grid's
 * current display values, including only editable (numeric) cells.
 * This is the payload passed to `useTableInputsEdit.mutate`.
 */
export function buildWritesFromGrid(
  rows: HydratedRow[],
  editedValues: Record<string, Record<string, number>>
): Record<string, number> {
  const writes: Record<string, number> = {};
  for (const row of rows) {
    for (const cell of row.cells) {
      if (!cell.editable || !cell.namedRange) continue;
      // Use user-edited value if present, otherwise the display value
      const edited = editedValues[row.rowLabel]?.[cell.colKey];
      if (edited !== undefined) {
        writes[cell.namedRange] = edited;
      } else if (typeof cell.displayValue === 'number') {
        writes[cell.namedRange] = cell.displayValue;
      } else if (typeof cell.displayValue === 'string') {
        const parsed = Number(cell.displayValue);
        if (Number.isFinite(parsed)) writes[cell.namedRange] = parsed;
      }
    }
  }
  return writes;
}

/**
 * Build the wire-format `rows` array for `POST /api/conversations/:id/table-edit`.
 * Maps the hydrated grid + user edits into `{ label, values }[]`.
 */
export function buildDisplayRows(
  rows: HydratedRow[],
  editedValues: Record<string, Record<string, number>>
): Array<{ label: string; values: Record<string, number> }> {
  return rows.map((row) => {
    const values: Record<string, number> = {};
    for (const cell of row.cells) {
      if (!cell.editable) continue;
      const edited = editedValues[row.rowLabel]?.[cell.colKey];
      if (edited !== undefined) {
        values[cell.colKey] = edited;
      } else if (typeof cell.displayValue === 'number') {
        values[cell.colKey] = cell.displayValue;
      } else if (typeof cell.displayValue === 'string') {
        const parsed = Number(cell.displayValue);
        if (Number.isFinite(parsed)) values[cell.colKey] = parsed;
      }
    }
    return { label: row.rowLabel, values };
  });
}
